import React, { useState } from "react";
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Grid,
    Typography,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Tabs,
    Tab,
    Paper,
    Container
} from "@mui/material";
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Upload as UploadIcon,
    Download as DownloadIcon,
    AutoStories
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../AdminLayout";
import learningService from "../../services/learningService";

const SAMPLE_JSON_FORMAT = {
    name: "Advanced React Fundamentals",
    description: "Learn React from basics to advanced concepts...",
    difficulty: "Intermediate",
    creditsOnCompletion: 25,
    duration: "6 weeks",
    levels: [
        {
            name: "Level 1: React Basics",
            description: "Understand JSX, Components, Props, and State",
            duration: "1 week",
            qualifyingScore: 70,
            questions: [
                {
                    text: "What is JSX?",
                    options: [
                        "A JavaScript XML syntax extension",
                        "A styling framework",
                        "A testing library",
                        "A package manager"
                    ],
                    correctAnswer: 0
                }
            ]
        }
    ]
};

export default function CreateLearningPathPage() {
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(0);
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        difficulty: "Beginner",
        creditsOnCompletion: 10,
        duration: "4 weeks"
    });
    const [levels, setLevels] = useState([]);
    const [levelForm, setLevelForm] = useState({
        name: "",
        description: "",
        duration: "30 mins",
        qualifyingScore: 70,
        questions: []
    });
    const [questionForm, setQuestionForm] = useState({
        text: "",
        options: ["", "", "", ""],
        correctAnswer: 0
    });
    const [openLevelDialog, setOpenLevelDialog] = useState(false);
    const [openQuestionDialog, setOpenQuestionDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [jsonInput, setJsonInput] = useState(JSON.stringify(SAMPLE_JSON_FORMAT, null, 2));

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleLevelChange = (e) => {
        const { name, value } = e.target;
        setLevelForm((prev) => ({
            ...prev,
            [name]: name === "qualifyingScore" ? parseInt(value) : value
        }));
    };

    const handleQuestionChange = (e) => {
        const { name, value } = e.target;
        setQuestionForm((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleOptionChange = (index, value) => {
        const options = [...questionForm.options];
        options[index] = value;
        setQuestionForm((prev) => ({
            ...prev,
            options
        }));
    };

    const addQuestion = () => {
        if (!questionForm.text || questionForm.options.some((o) => !o)) {
            setMessage("Please fill all question fields");
            return;
        }

        const questions = [...(levelForm.questions || [])];
        questions.push(questionForm);
        setLevelForm((prev) => ({
            ...prev,
            questions
        }));

        setQuestionForm({
            text: "",
            options: ["", "", "", ""],
            correctAnswer: 0
        });
        setOpenQuestionDialog(false);
    };

    const removeQuestion = (index) => {
        const questions = levelForm.questions.filter((_, i) => i !== index);
        setLevelForm((prev) => ({
            ...prev,
            questions
        }));
    };

    const addLevel = () => {
        if (!levelForm.name || levelForm.questions.length === 0) {
            setMessage("Please fill level name and add at least one question");
            return;
        }

        const newLevel = {
            id: `level_${Date.now()}`,
            ...levelForm
        };

        setLevels([...levels, newLevel]);
        setLevelForm({
            name: "",
            description: "",
            duration: "30 mins",
            qualifyingScore: 70,
            questions: []
        });
        setOpenLevelDialog(false);
        setMessage("");
    };

    const removeLevel = (index) => {
        setLevels(levels.filter((_, i) => i !== index));
    };

    const handleJsonImport = () => {
        try {
            const parsed = JSON.parse(jsonInput);
            setFormData({
                name: parsed.name,
                description: parsed.description,
                difficulty: parsed.difficulty,
                creditsOnCompletion: parsed.creditsOnCompletion,
                duration: parsed.duration
            });

            const importedLevels = parsed.levels.map((level) => ({
                id: `level_${Date.now()}_${Math.random()}`,
                name: level.name,
                description: level.description,
                duration: level.duration,
                qualifyingScore: level.qualifyingScore,
                questions: level.questions
            }));

            setLevels(importedLevels);
            setMessage("Learning path imported successfully from JSON!");
            setTabValue(0);
        } catch (error) {
            setMessage("Invalid JSON format. Please check and try again.");
        }
    };

    const handleJsonExport = () => {
        const exportData = {
            name: formData.name,
            description: formData.description,
            difficulty: formData.difficulty,
            creditsOnCompletion: formData.creditsOnCompletion,
            duration: formData.duration,
            levels: levels.map((l) => ({
                name: l.name,
                description: l.description,
                duration: l.duration,
                qualifyingScore: l.qualifyingScore,
                questions: l.questions
            }))
        };

        const dataStr = JSON.stringify(exportData, null, 2);
        const blob = new Blob([dataStr], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${formData.name}-learning-path.json`;
        a.click();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name || levels.length === 0) {
            setMessage("Please fill path details and add at least one level");
            return;
        }

        try {
            setLoading(true);
            setMessage("");

            const pathId = await learningService.createLearningPath({
                ...formData,
                levels
            });

            setMessage("Learning path created successfully!");
            setTimeout(() => {
                navigate("/admin/learning-paths");
            }, 1500);
        } catch (error) {
            setMessage(error.message || "Failed to create learning path");
        } finally {
            setLoading(false);
        }
    };

    return (
        <AdminLayout title="Create Learning Path">
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
                    Create New Learning Path
                </Typography>

                {message && (
                    <Alert
                        severity={message.includes("successfully") ? "success" : "error"}
                        sx={{ mb: 2 }}
                        onClose={() => setMessage("")}
                    >
                        {message}
                    </Alert>
                )}

                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
                    <Tab label="Builder" />
                    <Tab label="JSON Import/Export" />
                </Tabs>

                {/* Builder Tab */}
                {tabValue === 0 && (
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                                        Path Information
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Path Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleFormChange}
                                        required
                                    />

                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleFormChange}
                                        multiline
                                        rows={4}
                                    />

                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        select
                                        label="Difficulty Level"
                                        name="difficulty"
                                        value={formData.difficulty}
                                        onChange={handleFormChange}
                                    >
                                        <MenuItem value="Beginner">Beginner</MenuItem>
                                        <MenuItem value="Intermediate">Intermediate</MenuItem>
                                        <MenuItem value="Advanced">Advanced</MenuItem>
                                    </TextField>

                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        type="number"
                                        label="Credits on Completion"
                                        name="creditsOnCompletion"
                                        value={formData.creditsOnCompletion}
                                        onChange={handleFormChange}
                                    />

                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Duration"
                                        name="duration"
                                        value={formData.duration}
                                        onChange={handleFormChange}
                                        placeholder="e.g., 4 weeks"
                                    />
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                            Levels ({levels.length})
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            size="small"
                                            onClick={() => setOpenLevelDialog(true)}
                                        >
                                            Add Level
                                        </Button>
                                    </Box>

                                    {levels.length > 0 ? (
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: "primary.lighter" }}>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell align="right">Questions</TableCell>
                                                    <TableCell align="right">Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {levels.map((level, index) => (
                                                    <TableRow key={level.id}>
                                                        <TableCell>{level.name}</TableCell>
                                                        <TableCell align="right">
                                                            {level.questions?.length || 0}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => removeLevel(index)}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    ) : (
                                        <Alert severity="info">No levels added yet</Alert>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={22} /> : "Create Path"}
                                </Button>
                                <Button variant="outlined" onClick={() => navigate("/admin/learning-paths")}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleJsonExport}
                                    disabled={!formData.name}
                                >
                                    Export JSON
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                )}

                {/* JSON Tab */}
                {tabValue === 1 && (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                        <AutoStories /> JSON Format
                                    </Typography>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Paste or edit the JSON format below. You can also download a sample to understand the structure.
                                    </Alert>

                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={15}
                                        value={jsonInput}
                                        onChange={(e) => setJsonInput(e.target.value)}
                                        variant="outlined"
                                        sx={{ mb: 2, fontFamily: "monospace", fontSize: "12px" }}
                                    />

                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<UploadIcon />}
                                            onClick={handleJsonImport}
                                        >
                                            Import from JSON
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={() =>
                                                setJsonInput(JSON.stringify(SAMPLE_JSON_FORMAT, null, 2))
                                            }
                                        >
                                            Load Sample Format
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Level Dialog */}
                <Dialog open={openLevelDialog} onClose={() => setOpenLevelDialog(false)} maxWidth="md" fullWidth>
                    <DialogTitle>Add New Level</DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Level Name"
                                value={levelForm.name}
                                onChange={(e) =>
                                    setLevelForm((prev) => ({ ...prev, name: e.target.value }))
                                }
                            />

                            <TextField
                                fullWidth
                                margin="normal"
                                label="Level Description"
                                value={levelForm.description}
                                onChange={(e) =>
                                    setLevelForm((prev) => ({
                                        ...prev,
                                        description: e.target.value
                                    }))
                                }
                                multiline
                                rows={2}
                            />

                            <TextField
                                fullWidth
                                margin="normal"
                                label="Duration"
                                value={levelForm.duration}
                                onChange={(e) =>
                                    setLevelForm((prev) => ({ ...prev, duration: e.target.value }))
                                }
                            />

                            <TextField
                                fullWidth
                                margin="normal"
                                type="number"
                                label="Qualifying Score (%)"
                                value={levelForm.qualifyingScore}
                                onChange={(e) =>
                                    setLevelForm((prev) => ({
                                        ...prev,
                                        qualifyingScore: parseInt(e.target.value)
                                    }))
                                }
                                inputProps={{ min: 0, max: 100 }}
                            />

                            <Box sx={{ mt: 3 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 2
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Questions ({levelForm.questions?.length || 0})
                                    </Typography>
                                    <Button
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => setOpenQuestionDialog(true)}
                                    >
                                        Add Question
                                    </Button>
                                </Box>

                                {levelForm.questions?.map((q, index) => (
                                    <Card key={index} sx={{ mb: 1, p: 1 }}>
                                        <Typography variant="body2">
                                            <strong>Q{index + 1}:</strong> {q.text}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {q.options.length} options
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => removeQuestion(index)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Card>
                                ))}
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenLevelDialog(false)}>Cancel</Button>
                        <Button variant="contained" onClick={addLevel}>
                            Add Level
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Question Dialog */}
                <Dialog
                    open={openQuestionDialog}
                    onClose={() => setOpenQuestionDialog(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Add Question</DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Question Text"
                                value={questionForm.text}
                                onChange={handleQuestionChange}
                                name="text"
                                multiline
                                rows={2}
                            />

                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
                                Options
                            </Typography>
                            {questionForm.options.map((option, index) => (
                                <TextField
                                    key={index}
                                    fullWidth
                                    margin="normal"
                                    label={`Option ${index + 1}`}
                                    value={option}
                                    onChange={(e) => handleOptionChange(index, e.target.value)}
                                    size="small"
                                />
                            ))}

                            <TextField
                                fullWidth
                                margin="normal"
                                select
                                label="Correct Answer"
                                value={questionForm.correctAnswer}
                                onChange={(e) =>
                                    setQuestionForm((prev) => ({
                                        ...prev,
                                        correctAnswer: parseInt(e.target.value)
                                    }))
                                }
                            >
                                {questionForm.options.map((_, index) => (
                                    <MenuItem key={index} value={index}>
                                        Option {index + 1}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenQuestionDialog(false)}>Cancel</Button>
                        <Button variant="contained" onClick={addQuestion}>
                            Add Question
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </AdminLayout>
    );
}
